import { makeAutoObservable, runInAction } from "mobx";
import { fetchJsonFile } from "../../../helpers/files.ts";
import { IRootStore } from "../../../mobx/root-store.tsx";
import {
	ICatalogReport,
	IGenerationReport,
	IPartnerEarnings,
	ITopImages,
} from "../../../models/attribution-reports.ts";
import { convertDateToUtc, getTodayDateRange } from "../../../utils";
import QueryService from "../../../utils/QueryService.ts";
import useImageUtils from "../../../utils/useImageUtils.tsx";

export interface IAttributionReportsStore {
	selectedTab: string;
	selectedDateRange: [Date, Date];
	partnersEarningsPPGFile: File | null;
	topPaidImagesPPGFile: File | null;
	isError: boolean;
	generationReportData: IGenerationReport[];
	catalogReportData: ICatalogReport[];
	catalogReportBrai2Data: ICatalogReport[];
	catalogReportBrai3Data: ICatalogReport[];
	catalogReportBrai4Data: ICatalogReport[];
	PartnersEarningsReportBrai2Data: IPartnerEarnings[];
	PartnersEarningsReportBrai3Data: IPartnerEarnings[];
	generationReportBrai2Data: IGenerationReport[];
	generationReportBrai3Data: IGenerationReport[];
	generationReportBrai4Data: IGenerationReport[];
	topImagesData: ITopImages[];
	partnersEarningsData: IPartnerEarnings[];
	isGenerationReportDataLoading: boolean;
	isCatalogReportDataLoading: boolean;
	isTopImagesDataLoading: boolean;
	isTopHitsImagesDataLoading: boolean;
	isPartnersEarningsDataLoading: boolean;
	isExportReportLoading: boolean;

	resetStore: () => void;
	setSelectedTab: (selectedTabl: string) => void;
	loadGenerationReportData: () => void;
	loadCatalogReportByModelVersion: () => Promise<ICatalogReport[]>;
	loadCatalogReportData: (minCacheDate?: Date) => Promise<ICatalogReport[]>;
	loadTopHitsImagesData: () => void;
	loadTopPaidImagesData: () => void;
	loadPartnersEarningsData: () => void;
}

export default class AttributionReportsStore implements IAttributionReportsStore {
	private queryService: QueryService = new QueryService("/attribution_reports");
	private imageUtils = useImageUtils();

	rootStore: IRootStore;
	selectedTab: string = "";
	selectedDateRange: [Date, Date] = getTodayDateRange();
	partnersEarningsPPGFile: File | null = null;
	topPaidImagesPPGFile: File | null = null;
	isError: boolean = false;
	generationReportData: IGenerationReport[] = [];
	catalogReportData: ICatalogReport[] = [];
	catalogReportBrai2Data: ICatalogReport[] = [];
	catalogReportBrai3Data: ICatalogReport[] = [];
	catalogReportBrai4Data: ICatalogReport[] = [];
	PartnersEarningsReportBrai2Data: IPartnerEarnings[] = [];
	PartnersEarningsReportBrai3Data: IPartnerEarnings[] = [];
	generationReportBrai2Data: IGenerationReport[] = [];
	generationReportBrai3Data: IGenerationReport[] = [];
	generationReportBrai4Data: IGenerationReport[] = [];
	topImagesData: ITopImages[] = [];
	partnersEarningsData: IPartnerEarnings[] = [];
	isGenerationReportDataLoading: boolean = false;
	isCatalogReportDataLoading: boolean = false;
	isTopImagesDataLoading: boolean = false;
	isTopHitsImagesDataLoading: boolean = false;
	isPartnersEarningsDataLoading: boolean = false;
	isExportReportLoading: boolean = false;

	constructor(rootStore: IRootStore) {
		makeAutoObservable(this);
		this.rootStore = rootStore;
	}

	resetStore() {
		this.partnersEarningsPPGFile = null;
		this.topPaidImagesPPGFile = null;
		this.generationReportData = [];
		this.catalogReportData = [];
		this.catalogReportBrai2Data = [];
		this.catalogReportBrai3Data = [];
		this.catalogReportBrai4Data = [];
		this.PartnersEarningsReportBrai2Data = [];
		this.PartnersEarningsReportBrai3Data = [];
		this.generationReportBrai2Data = [];
		this.generationReportBrai3Data = [];
		this.generationReportBrai4Data = [];
		this.topImagesData = [];
		this.partnersEarningsData = [];
		this.isGenerationReportDataLoading = false;
		this.isCatalogReportDataLoading = false;
		this.isTopImagesDataLoading = false;
		this.isTopHitsImagesDataLoading = false;
		this.isPartnersEarningsDataLoading = false;
		this.isExportReportLoading = false;
	}

	private datesPayload() {
		return {
			startDate: convertDateToUtc(this.selectedDateRange[0]).toISOString(),
			endDate: convertDateToUtc(this.selectedDateRange[1]).toISOString(),
		};
	}

	setSelectedTab = (selectedTab: string) => {
		runInAction(() => {
			this.selectedTab = selectedTab;
		});
	};

	loadGenerationReportData = async () => {
		if (this.selectedDateRange.length !== 2) return;
		try {
			runInAction(() => {
				this.isGenerationReportDataLoading = true;
			});
			const data: any = await this.queryService.post("/generation", this.datesPayload());
			await this.imageUtils.pullUntilAvailable(data.result_url, { maxAttempts: 600, sleepSecs: 5 });
			const jsonData = await fetchJsonFile(data.result_url);
			if (jsonData.error) {
				throw new Error(jsonData.error);
			}
			runInAction(() => {
				this.generationReportData = jsonData.main;
				this.generationReportBrai2Data = jsonData.bria2Report;
				this.generationReportBrai3Data = jsonData.bria3Report;
				this.generationReportBrai4Data = jsonData.bria4Report;
			});
		} catch (e) {
			console.log("error while generating attribution report", e);
			runInAction(() => {
				this.isError = true;
			});
			return Promise.reject(e);
		} finally {
			runInAction(() => {
				this.isGenerationReportDataLoading = false;
			});
		}
	};

	loadCatalogReportByModelVersion = async () => {
		if (this.selectedDateRange.length !== 2) return [];
		try {
			runInAction(() => {
				this.isCatalogReportDataLoading = true;
			});

			const data: any = await this.queryService.post("/catalog_by_model", this.datesPayload());
			await this.imageUtils.pullUntilAvailable(data.result_url, { maxAttempts: 600, sleepSecs: 5 });
			const jsonData = await fetchJsonFile(data.result_url);

			if (jsonData.error) {
				throw new Error(jsonData.error);
			}

			runInAction(() => {
				this.catalogReportData = jsonData.main;
				this.catalogReportBrai2Data = jsonData.bria2Catalog;
				this.catalogReportBrai3Data = jsonData.bria3Catalog;
				this.catalogReportBrai4Data = jsonData.bria4Catalog;
			});

			return this.catalogReportData;
		} catch (e) {
			console.log("error while generating attribution report", e);
			runInAction(() => {
				this.isError = true;
			});
			return Promise.reject(e);
		} finally {
			runInAction(() => {
				this.isCatalogReportDataLoading = false;
			});
		}
	};

	loadCatalogReportData = async (minCacheDate: Date = new Date()) => {
		if (this.selectedDateRange.length !== 2) return [];
		try {
			runInAction(() => {
				this.isCatalogReportDataLoading = true;
			});

			const data: any = await this.queryService.post("/catalog", {
				...this.datesPayload(),
				minCacheDate: minCacheDate && convertDateToUtc(minCacheDate).toISOString(),
			});
			await this.imageUtils.pullUntilAvailable(data.result_url, { maxAttempts: 600, sleepSecs: 5 });
			const jsonData = await fetchJsonFile(data.result_url);

			if (jsonData.error) {
				throw new Error(jsonData.error);
			}

			runInAction(() => {
				this.catalogReportData = jsonData;
			});

			return this.catalogReportData;
		} catch (e) {
			console.log("error while generating attribution report", e);
			runInAction(() => {
				this.isError = true;
			});
			return Promise.reject(e);
		} finally {
			runInAction(() => {
				this.isCatalogReportDataLoading = false;
			});
		}
	};

	loadTopHitsImagesData = async () => {
		if (this.selectedDateRange.length !== 2) return;
		try {
			runInAction(() => {
				this.isTopHitsImagesDataLoading = true;
			});
			const data: any = await this.queryService.post("/top_hits_images", this.datesPayload());
			await this.imageUtils.pullUntilAvailable(data.result_url, { maxAttempts: 600, sleepSecs: 5 });
			const jsonData = await fetchJsonFile(data.result_url);
			if (jsonData.error) {
				throw new Error(jsonData.error);
			}

			runInAction(() => {
				this.topImagesData = jsonData;
			});
		} catch (e) {
			console.log("error while generating attribution report", e);
			runInAction(() => {
				this.isError = true;
			});
			return Promise.reject(e);
		} finally {
			runInAction(() => {
				this.isTopHitsImagesDataLoading = false;
			});
		}
	};

	loadTopPaidImagesData = async () => {
		if (
			this.selectedDateRange.length !== 2 ||
			this.topPaidImagesPPGFile === null ||
			!this.rootStore.authStore.user?.isSuperAdmin()
		) {
			return;
		}

		try {
			runInAction(() => {
				this.isTopImagesDataLoading = true;
			});
			const formData = new FormData();
			formData.append("ppgFile", this.topPaidImagesPPGFile);
			formData.append("startDate", this.datesPayload().startDate);
			formData.append("endDate", this.datesPayload().endDate);
			const data: any = await this.queryService.post("/top_paid_images", formData, {
				"Content-Type": "multipart/form-data",
			});
			await this.imageUtils.pullUntilAvailable(data.result_url, { maxAttempts: 600, sleepSecs: 5 });
			const jsonData = await fetchJsonFile(data.result_url);
			if (jsonData.error) {
				throw new Error(jsonData.error);
			}

			runInAction(() => {
				this.topImagesData = this.topImagesData.map((item: ITopImages) => ({
					...item,
					topPaid: jsonData[item.partner] || [],
				}));
			});
		} catch (e) {
			console.log("error while generating attribution report", e);
			runInAction(() => {
				this.isError = true;
			});
			return Promise.reject(e);
		} finally {
			runInAction(() => {
				this.isTopImagesDataLoading = false;
			});
		}
	};

	loadPartnersEarningsData = async () => {
		if (
			this.selectedDateRange.length !== 2 ||
			this.partnersEarningsPPGFile === null ||
			!this.rootStore.authStore.user?.isSuperAdmin()
		) {
			return;
		}
		try {
			runInAction(() => {
				this.isPartnersEarningsDataLoading = true;
			});
			const formData = new FormData();
			formData.append("ppgFile", this.partnersEarningsPPGFile);
			formData.append("startDate", this.datesPayload().startDate);
			formData.append("endDate", this.datesPayload().endDate);
			const data: any = await this.queryService.post("/partners_earnings", formData, {
				"Content-Type": "multipart/form-data",
			});
			await this.imageUtils.pullUntilAvailable(data.result_url, { maxAttempts: 600, sleepSecs: 5 });
			const jsonData = await fetchJsonFile(data.result_url);
			if (jsonData.error) {
				throw new Error(jsonData.error);
			}

			runInAction(() => {
				this.partnersEarningsData = jsonData.main;
				this.PartnersEarningsReportBrai2Data = jsonData.bria2PartnersEarnings;
				this.PartnersEarningsReportBrai3Data = jsonData.bria3PartnersEarnings;
			});
		} catch (e) {
			console.log("error while generating attribution report", e);
			runInAction(() => {
				this.isError = true;
			});
			return Promise.reject(e);
		} finally {
			runInAction(() => {
				this.isPartnersEarningsDataLoading = false;
			});
		}
	};
}
